import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DataStandard, SchemaCategory, SchemaField, DataStandardVersion, FieldConstraints } from '@/lib/data-standards/schemas';
import { Json } from '@/integrations/supabase/types';
import { 
  generateSchema, 
  generateExample, 
  type FieldMapping, 
  type FieldLibraryItem,
  type FieldDataType 
} from '@/lib/admin/field-utils';

// Type for the nested query response
interface FieldMappingWithLibrary {
  id: string;
  is_required: boolean;
  display_order: number | null;
  lookup_group: string | null;
  inline_enum: Json | null;
  default_value: string | null;
  notes: string | null;
  field_library: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    data_type: string;
    format: string | null;
    pattern: string | null;
    min_length: number | null;
    max_length: number | null;
    minimum: number | null;
    maximum: number | null;
    example_value: string | null;
  };
}

// Fetch function extracted for reuse
async function fetchDataStandards(): Promise<{
  schemas: DataStandard[];
  categories: SchemaCategory[];
}> {
  const { data, error: fetchError } = await supabase
    .from('data_standards')
    .select(`
      id,
      slug,
      title,
      description,
      version,
      category,
      category_name,
      display_order,
      is_active,
      maturity_level,
      deprecation_notice,
      updated_at,
      standard_field_mappings (
        id,
        is_required,
        display_order,
        lookup_group,
        inline_enum,
        default_value,
        notes,
        field_library (
          id,
          slug,
          name,
          description,
          data_type,
          format,
          pattern,
          min_length,
          max_length,
          minimum,
          maximum,
          example_value
        )
      )
    `)
    .eq('is_active', true)
    .order('display_order');

  if (fetchError) throw fetchError;

  if (!data || data.length === 0) {
    return { schemas: [], categories: [] };
  }

  // Collect all lookup groups from all standards
  const allLookupGroups = new Set<string>();
  data.forEach(item => {
    const mappings = (item.standard_field_mappings as unknown as FieldMappingWithLibrary[]) || [];
    mappings.forEach(m => {
      if (m.lookup_group) allLookupGroups.add(m.lookup_group);
    });
  });

  // Fetch all lookup values in one batch query
  let lookupsByGroup: Record<string, string[]> = {};
  if (allLookupGroups.size > 0) {
    const { data: lookupData } = await supabase
      .from('lookups')
      .select('lookup_group, value')
      .in('lookup_group', Array.from(allLookupGroups))
      .eq('is_active', true)
      .order('display_order');

    if (lookupData) {
      lookupsByGroup = lookupData.reduce((acc, item) => {
        if (!acc[item.lookup_group]) acc[item.lookup_group] = [];
        acc[item.lookup_group].push(item.value);
        return acc;
      }, {} as Record<string, string[]>);
    }
  }

  // Build constraints object, filtering out undefined values
  const buildConstraints = (fl: FieldMappingWithLibrary['field_library']): FieldConstraints | undefined => {
    const constraints: FieldConstraints = {};
    if (fl.min_length != null) constraints.minLength = fl.min_length;
    if (fl.max_length != null) constraints.maxLength = fl.max_length;
    if (fl.minimum != null) constraints.minimum = Number(fl.minimum);
    if (fl.maximum != null) constraints.maximum = Number(fl.maximum);
    if (fl.pattern) constraints.pattern = fl.pattern;
    return Object.keys(constraints).length > 0 ? constraints : undefined;
  };

  // Transform database records to match existing interface
  const transformedSchemas: DataStandard[] = data.map((item) => {
    // Cast and filter field mappings
    const mappingsRaw = (item.standard_field_mappings as unknown as FieldMappingWithLibrary[] || [])
      .filter((mapping) => mapping.field_library)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

    // Convert to FieldMapping format for generator functions
    const fieldMappings: FieldMapping[] = mappingsRaw.map((m) => ({
      id: m.id,
      field_id: m.field_library.id,
      field: {
        id: m.field_library.id,
        slug: m.field_library.slug,
        name: m.field_library.name,
        description: m.field_library.description,
        data_type: m.field_library.data_type as FieldDataType,
        format: m.field_library.format,
        pattern: m.field_library.pattern,
        min_length: m.field_library.min_length,
        max_length: m.field_library.max_length,
        minimum: m.field_library.minimum,
        maximum: m.field_library.maximum,
        is_common: false,
        display_order: m.display_order,
        example_value: m.field_library.example_value,
      } as FieldLibraryItem,
      is_required: m.is_required,
      default_value: m.default_value,
      lookup_group: m.lookup_group,
      inline_enum: m.inline_enum as string[] | null,
      display_order: m.display_order ?? 0,
      notes: m.notes,
    }));

    // Generate schema and example dynamically using the SAME functions as admin
    const schema = generateSchema(fieldMappings, {
      title: item.title,
      description: item.description || undefined,
      lookupData: lookupsByGroup,
    });

    const example = generateExample(fieldMappings, lookupsByGroup);

    // Build SchemaField array for field reference display
    const fields: SchemaField[] = mappingsRaw.map((mapping) => ({
      name: mapping.field_library.slug,
      type: mapping.field_library.data_type,
      description: mapping.field_library.description || '',
      required: mapping.is_required,
      format: mapping.field_library.format || undefined,
      lookupGroup: mapping.lookup_group || undefined,
      constraints: buildConstraints(mapping.field_library),
    }));

    return {
      id: item.slug,
      title: item.title,
      description: item.description || '',
      version: item.version,
      category: item.category,
      lastUpdated: item.updated_at.split('T')[0],
      schema,
      example,
      fields,
    };
  });

  // Fetch categories for icons
  const { data: categoryData } = await supabase
    .from('categories')
    .select('slug, icon')
    .order('display_order');

  const categoryIcons = categoryData?.reduce((acc, cat) => {
    acc[cat.slug] = cat.icon;
    return acc;
  }, {} as Record<string, string | null>) || {};

  // Group by category
  const categoryMap = new Map<string, SchemaCategory>();
  data.forEach((item) => {
    if (!categoryMap.has(item.category)) {
      categoryMap.set(item.category, {
        id: item.category,
        name: item.category_name,
        description: '',
        icon: categoryIcons[item.category] || undefined,
        schemas: [],
      });
    }
    const cat = categoryMap.get(item.category)!;
    const schema = transformedSchemas.find(s => s.id === item.slug);
    if (schema) {
      cat.schemas.push(schema);
    }
  });

  return {
    schemas: transformedSchemas,
    categories: Array.from(categoryMap.values()),
  };
}

export function useDataStandards() {
  const queryClient = useQueryClient();
  
  const { data, isLoading, error, refetch: queryRefetch } = useQuery({
    queryKey: ['data-standards'],
    queryFn: fetchDataStandards,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,   // Keep in cache 30 minutes
  });

  const schemas = data?.schemas ?? [];
  const categories = data?.categories ?? [];

  const getSchemaById = useCallback((id: string): DataStandard | undefined => {
    return schemas.find(s => s.id === id);
  }, [schemas]);

  const fetchVersions = useCallback(async (standardSlug: string): Promise<DataStandardVersion[]> => {
    // First get the UUID from the slug
    const { data: standardData } = await supabase
      .from('data_standards')
      .select('id')
      .eq('slug', standardSlug)
      .single();

    if (!standardData) return [];

    const { data: versionsData, error: versionsError } = await supabase
      .from('data_standard_versions')
      .select('id, standard_id, version, schema, example, fields, changelog, published_at, published_by, is_current')
      .eq('standard_id', standardData.id)
      .order('published_at', { ascending: false });
    
    if (versionsError || !versionsData) return [];

    return versionsData.map(v => ({
      id: v.id,
      standardId: v.standard_id,
      version: v.version,
      schema: v.schema as object,
      example: v.example as object,
      fields: (v.fields as Json[])?.map((f) => f as unknown as SchemaField) || [],
      changelog: v.changelog,
      publishedAt: v.published_at,
      publishedBy: v.published_by,
      isCurrent: v.is_current,
    }));
  }, []);

  const getVersionData = useCallback(async (standardSlug: string, version: string): Promise<DataStandardVersion | null> => {
    // First get the UUID from the slug
    const { data: standardData } = await supabase
      .from('data_standards')
      .select('id')
      .eq('slug', standardSlug)
      .single();

    if (!standardData) return null;

    const { data: versionData, error: versionError } = await supabase
      .from('data_standard_versions')
      .select('id, standard_id, version, schema, example, fields, changelog, published_at, published_by, is_current')
      .eq('standard_id', standardData.id)
      .eq('version', version)
      .single();
    
    if (versionError || !versionData) return null;

    return {
      id: versionData.id,
      standardId: versionData.standard_id,
      version: versionData.version,
      schema: versionData.schema as object,
      example: versionData.example as object,
      fields: (versionData.fields as Json[])?.map((f) => f as unknown as SchemaField) || [],
      changelog: versionData.changelog,
      publishedAt: versionData.published_at,
      publishedBy: versionData.published_by,
      isCurrent: versionData.is_current,
    };
  }, []);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['data-standards'] });
  }, [queryClient]);

  return {
    schemas,
    categories,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch data standards') : null,
    getSchemaById,
    refetch,
    fetchVersions,
    getVersionData,
  };
}